// pages/admin/complaints/AdminComplaintsList.jsx
import { FiClock, FiCheckCircle, FiMessageSquare, FiUser, FiBriefcase, FiEye } from "react-icons/fi";
import { useTranslation } from "@/hooks/useTranslation";

const formatDate = (value) =>
    value
        ? new Date(value).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
        : "-";

export const ComplaintStatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const statusConfig = {
        OPEN: {
            icon: <FiClock className="w-4 h-4" />,
            color: "bg-yellow-100 text-yellow-800 border-yellow-200",
            label: t("complaints.status.OPEN"),
        },
        ANSWERED: {
            icon: <FiMessageSquare className="w-4 h-4" />,
            color: "bg-blue-100 text-blue-800 border-blue-200",
            label: t("complaints.status.ANSWERED"),
        },
        RESOLVED: {
            icon: <FiCheckCircle className="w-4 h-4" />,
            color: "bg-green-100 text-green-800 border-green-200",
            label: t("complaints.status.RESOLVED"),
        },
    };

    const config = statusConfig[status] || statusConfig.OPEN;

    return (
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border whitespace-nowrap ${config.color}`}>
            {config.icon}
            {config.label}
        </span>
    );
};

const ClientAvatar = ({ author }) => (
    <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center overflow-hidden">
        {author.profilePictureUrl ? (
            <img src={author.profilePictureUrl} alt="" className="h-full w-full object-cover" />
        ) : (
            <FiUser className="w-5 h-5 text-blue-600" />
        )}
    </div>
);

const AwaitingBadge = () => {
    const { t } = useTranslation();
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-200 whitespace-nowrap">
            {t("complaints.awaitingReply")}
        </span>
    );
};

const ViewButton = ({ onClick }) => {
    const { t } = useTranslation();
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-cyan-600 bg-cyan-50 border border-cyan-200 rounded-lg hover:bg-cyan-100 hover:border-cyan-300 transition-colors whitespace-nowrap"
        >
            <FiEye className="w-4 h-4" />
            {t("complaints.view")}
        </button>
    );
};
const getRowData = (complaint, t) => {
    const author = complaint.author || {};
    return {
        author,
        authorName:
            [author.firstname, author.lastname].filter(Boolean).join(" ") ||
            t("complaints.unknownUser", { id: author.id }),
        companyName: complaint.company?.companyName,
        categoryLabel: complaint.category ? t(`complaints.category.${complaint.category}`) : "-",
        awaitingAdmin: complaint.status !== "RESOLVED" && !complaint.lastMessageFromAdmin,
        lastActivity: formatDate(complaint.lastMessageAt || complaint.createdAt),
    };
};

export const AdminComplaintsList = ({ complaints, onViewDetails }) => {
    const { t } = useTranslation();

    const thClass =
        "px-4 py-3 text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider";

    return (
        <>
            {/* Table: large screens */}
            <div className="hidden xl:block overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className={thClass}>{t("complaints.tableSubject")}</th>
                        <th className={thClass}>{t("complaints.tableClient")}</th>
                        <th className={thClass}>{t("complaints.tableStatus")}</th>
                        <th className={thClass}>{t("complaints.tableMessages")}</th>
                        <th className={thClass}>{t("complaints.tableLastActivity")}</th>
                        <th className={`${thClass} text-right rtl:text-left`}>{t("complaints.tableActions")}</th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {complaints.map((complaint) => {
                        const row = getRowData(complaint, t);
                        return (
                            <tr
                                key={complaint.id}
                                onClick={() => onViewDetails(complaint)}
                                className="hover:bg-gray-50 transition-colors cursor-pointer"
                            >
                                <td className="px-4 py-4 max-w-xs">
                                    <div className="text-sm font-semibold text-gray-900 truncate">
                                        {complaint.subject}
                                    </div>
                                    <div className="text-xs text-gray-500">{row.categoryLabel}</div>
                                </td>
                                <td className="px-4 py-4">
                                    <div className="flex items-center min-w-0">
                                        <ClientAvatar author={row.author} />
                                        <div className="ml-3 rtl:ml-0 rtl:mr-3 min-w-0">
                                            <div className="text-sm font-semibold text-gray-900 truncate">
                                                {row.authorName}
                                            </div>
                                            <div className="text-xs text-gray-500 truncate">{row.author.email}</div>
                                            {row.companyName && (
                                                <div className="flex items-center gap-1 text-xs text-gray-500 truncate">
                                                    <FiBriefcase className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                    {row.companyName}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <ComplaintStatusBadge status={complaint.status} />
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex flex-col items-start gap-1">
                                        <span className="flex items-center gap-1.5 text-sm text-gray-700">
                                            <FiMessageSquare className="w-4 h-4 text-gray-400" />
                                            {complaint.messageCount ?? 0}
                                        </span>
                                        {row.awaitingAdmin && <AwaitingBadge />}
                                    </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {row.lastActivity}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-right rtl:text-left">
                                    <ViewButton onClick={() => onViewDetails(complaint)} />
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>

            {/* Cards: small and medium screens */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 xl:hidden">
                {complaints.map((complaint) => {
                    const row = getRowData(complaint, t);
                    return (
                        <div
                            key={complaint.id}
                            onClick={() => onViewDetails(complaint)}
                            className="flex flex-col gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm hover:border-gray-300 transition cursor-pointer"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-gray-900 break-words">
                                        {complaint.subject}
                                    </div>
                                    <div className="text-xs text-gray-500">{row.categoryLabel}</div>
                                </div>
                                <ComplaintStatusBadge status={complaint.status} />
                            </div>

                            <div className="flex items-center min-w-0">
                                <ClientAvatar author={row.author} />
                                <div className="ml-3 rtl:ml-0 rtl:mr-3 min-w-0">
                                    <div className="text-sm font-semibold text-gray-900 truncate">{row.authorName}</div>
                                    <div className="text-xs text-gray-500 truncate">
                                        {row.companyName || row.author.email}
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-gray-100">
                                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                    <span className="flex items-center gap-1">
                                        <FiMessageSquare className="w-3.5 h-3.5" />
                                        {complaint.messageCount ?? 0}
                                    </span>
                                    <span>{row.lastActivity}</span>
                                    {row.awaitingAdmin && <AwaitingBadge />}
                                </div>
                                <ViewButton onClick={() => onViewDetails(complaint)} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};